import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { childConnectionService } from '@/services/childConnectionService';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';

interface ChildTeam {
  team_id: number;
  team_name: string;
  sport_id: string;
  slug: string;
  logo_url?: string;
  age_group: string;
  is_child_team: boolean;
  child_id: string;
}

const ChildTeams: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [teams, setTeams] = useState<ChildTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [childInfo, setChildInfo] = useState<any>(null);
  
  const childId = searchParams.get('child');

  useEffect(() => {
    if (childId) {
      loadChildTeams();
      loadChildInfo();
    } else {
      toast({
        title: "Invalid Request",
        description: "No child ID provided",
        variant: "destructive"
      });
      navigate('/child-management');
    }
  }, [childId]);

  const loadChildTeams = async () => {
    if (!childId) return;
    
    setLoading(true);
    try {
      const childTeams = await childConnectionService.getChildTeams(childId);
      console.log('🎯 Child teams loaded:', childTeams);
      setTeams(childTeams);
    } catch (error) {
      console.error('❌ Error loading child teams:', error);
      toast({
        title: "Loading Error",
        description: "Failed to load child's teams",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadChildInfo = async () => {
    if (!childId) return;
    
    try {
      // Get child info from internal_users table
      const response = await fetch(`https://gmmfphwrwhvixldazcmp.supabase.co/rest/v1/internal_users?select=id,username,role,age_group&id=eq.${childId}`, {
        headers: {
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdtbWZwaHdyd2h2aXhsZGF6Y21wIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY2OTc0NDQsImV4cCI6MjA2MjI3MzQ0NH0.OYfeYW4ehGkrFZ1WW2j21sTPtg_XxaeDgNA722rDqqk',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdtbWZwaHdyd2h2aXhsZGF6Y21wIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY2OTc0NDQsImV4cCI6MjA2MjI3MzQ0NH0.OYfeYW4ehGkrFZ1WW2j21sTPtg_XxaeDgNA722rDqqk',
          'Accept': 'application/vnd.pgrst.object+json'
        }
      });
      const childData = await response.json();
      setChildInfo(childData);
    } catch (error) {
      console.error('❌ Error loading child info:', error);
    }
  };

  const handleTeamClick = (team: ChildTeam) => {
    navigate(`/team/${team.slug}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {isMobile ? <MobileHeader /> : <Header />}
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/child-management')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Child Management
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-8 w-8 text-primary" />
              {childInfo?.username}'s Teams
            </h1>
            <p className="text-muted-foreground mt-2">
              View all teams that {childInfo?.username} is part of
            </p>
          </div>
        </div>

        {/* Teams Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-3 bg-muted rounded mb-4 w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : teams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => (
              <Card 
                key={team.team_id} 
                className="hover:shadow-lg transition-shadow cursor-pointer border-primary/20"
                onClick={() => handleTeamClick(team)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    {team.logo_url ? (
                      <img 
                        src={team.logo_url} 
                        alt={`${team.team_name} logo`}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{team.team_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{team.sport_id}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      Age Group: {team.age_group}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      Team ID: {team.team_id}
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full mt-4"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTeamClick(team);
                    }}
                  >
                    View Team Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Teams Found</h3>
              <p className="text-muted-foreground">
                {childInfo?.username} hasn't joined any teams yet.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      {isMobile ? <MobileBottomNav /> : <Footer />}
    </div>
  );
};

export default ChildTeams;